import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Offcanvas } from 'bootstrap';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import {
  BankAccount,
  BankAccountSequence,
  DEFAULT_INVOICE_FORMAT,
  invoiceFormatError,
  previewInvoiceId,
} from '../bank-account.model';

function invoiceFormatValidator(control: AbstractControl): ValidationErrors | null {
  const message = invoiceFormatError(control.value ?? '');
  return message ? { invoiceFormat: message } : null;
}

@Component({
  selector: 'app-bank-accounts-list',
  standalone: true,
  imports: [NgFor, NgIf, ReactiveFormsModule, BreadcrumbComponent, PaginationComponent],
  templateUrl: './bank-accounts-list.component.html',
  styleUrls: ['./bank-accounts-list.component.css'],
})
export class BankAccountsListComponent implements OnInit {
  @ViewChild('bankAccountModal') modalEl!: ElementRef;
  @ViewChild('bankViewModal') viewModalEl!: ElementRef;
  @ViewChild('invoiceFormatInput') invoiceFormatInput?: ElementRef<HTMLInputElement>;
  private offcanvas: Offcanvas | null = null;
  private viewOffcanvas: Offcanvas | null = null;

  readonly formatPlaceholders = ['{{financialYear}}', '{{autoId}}', '{{year}}', '{{month}}'];

  viewing: BankAccount | null = null;
  viewSequences: BankAccountSequence[] = [];
  sequencesLoading = false;
  resyncing = false;

  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Bank Accounts',
    breadcrumb_url: '/admin',
  };

  bankAccounts: BankAccount[] = [];
  total = 0;
  pageCount = 1;
  page = 1;
  limit = 20;
  search = '';

  form!: FormGroup;
  editingUid: string | null = null;

  canAdd = false;
  canUpdate = false;
  canDelete = false;

  loadingService = inject(LoadingService);
  private httpService = inject(HttpServiceService);
  private auth = inject(LocalStorageService);
  private alert = inject(NgxToastrService);
  private fb = inject(FormBuilder);

  ngOnInit(): void {
    this.canAdd = this.auth.checkPermission(USER_PERMISSIONS.BANK_ACCOUNTS.BANK_ACCOUNT_ADD);
    this.canUpdate = this.auth.checkPermission(USER_PERMISSIONS.BANK_ACCOUNTS.BANK_ACCOUNT_UPDATE);
    this.canDelete = this.auth.checkPermission(USER_PERMISSIONS.BANK_ACCOUNTS.BANK_ACCOUNT_DELETE);
    this.initForm();
    this.load();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.modalEl?.nativeElement) {
        this.offcanvas = Offcanvas.getOrCreateInstance(this.modalEl.nativeElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }
      if (this.viewModalEl?.nativeElement) {
        this.viewOffcanvas = Offcanvas.getOrCreateInstance(this.viewModalEl.nativeElement, {
          backdrop: 'static',
          keyboard: false,
        });
      }
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      bank_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      display_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      invoice_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      invoice_format: [
        DEFAULT_INVOICE_FORMAT,
        [Validators.required, Validators.maxLength(100), invoiceFormatValidator],
      ],
      gstin: ['', [Validators.maxLength(20)]],
      logo: [''],
      address: [''],
      phone: [''],
      email: ['', [Validators.email]],
      account_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      branch_name: [''],
      account_number: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(40)]],
      ifsc_code: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(20)]],
    });
  }

  load(): void {
    this.httpService.getBankAccounts(this.page, this.limit, this.search).subscribe({
      next: (resp) => {
        if (resp.success) {
          this.bankAccounts = resp.data?.bank_accounts ?? [];
          this.total = resp.data?.total ?? 0;
          this.pageCount = resp.data?.pageCount ?? 1;
        }
      },
      error: () => this.alert.error('Failed to load bank accounts', 'toast-top-right'),
    });
  }

  onSearchChange(value: string): void {
    this.search = value;
    this.page = 1;
    this.load();
  }

  pageChange(p: number): void {
    if (this.page === p) return;
    this.page = p;
    this.load();
  }

  openAdd(): void {
    this.editingUid = null;
    this.form.reset({
      bank_name: '',
      display_name: '',
      invoice_name: '',
      invoice_format: DEFAULT_INVOICE_FORMAT,
      gstin: '',
      logo: '',
      address: '',
      phone: '',
      email: '',
      account_name: '',
      branch_name: '',
      account_number: '',
      ifsc_code: '',
    });
    this.offcanvas?.show();
  }

  openEdit(account: BankAccount): void {
    this.editingUid = account.id;
    this.form.patchValue({
      bank_name: account.bank_name,
      display_name: account.display_name,
      invoice_name: account.invoice_name,
      invoice_format: account.invoice_format || DEFAULT_INVOICE_FORMAT,
      gstin: account.gstin ?? '',
      logo: account.logo ?? '',
      address: account.address ?? '',
      phone: account.phone ?? '',
      email: account.email ?? '',
      account_name: account.account_name,
      branch_name: account.branch_name ?? '',
      account_number: account.account_number,
      ifsc_code: account.ifsc_code,
    });
    this.offcanvas?.show();
  }

  close(): void {
    this.offcanvas?.hide();
  }

  openView(account: BankAccount): void {
    this.viewing = account;
    this.viewSequences = [];
    this.loadSequences(account.id);
    this.viewOffcanvas?.show();
  }

  closeView(): void {
    this.viewOffcanvas?.hide();
    this.viewing = null;
  }

  editFromView(): void {
    const account = this.viewing;
    this.closeView();
    if (account) this.openEdit(account);
  }

  private loadSequences(uid: string): void {
    this.sequencesLoading = true;
    this.httpService.getBankAccountSequences(uid).subscribe({
      next: (resp) => {
        this.sequencesLoading = false;
        if (resp.success) this.viewSequences = resp.data?.sequences ?? [];
      },
      error: () => {
        this.sequencesLoading = false;
        this.alert.error('Failed to load invoice sequences', 'toast-top-right');
      },
    });
  }

  recalculateSequences(): void {
    if (!this.viewing || this.resyncing) return;
    if (
      !confirm(
        'Recalculate invoice numbering from live invoices? Use this after an invoice was removed directly in the database.'
      )
    )
      return;
    this.resyncing = true;
    this.httpService.resyncBankAccountSequences(this.viewing.id).subscribe({
      next: (resp) => {
        this.resyncing = false;
        this.viewSequences = resp.data?.sequences ?? [];
        this.alert.success(resp?.message || 'Sequences recalculated', 'toast-top-right');
      },
      error: (err) => {
        this.resyncing = false;
        const msg = err?.error?.message || 'Failed to recalculate sequences';
        this.alert.error(msg, 'toast-top-right');
      },
    });
  }

  insertPlaceholder(token: string): void {
    const control = this.form.get('invoice_format');
    const input = this.invoiceFormatInput?.nativeElement;
    const current: string = control?.value ?? '';
    const pos =
      input && typeof input.selectionStart === 'number' ? input.selectionStart : current.length;
    control?.setValue(current.slice(0, pos) + token + current.slice(pos));
    control?.markAsDirty();
    setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(pos + token.length, pos + token.length);
    });
  }

  get formatPreview(): string {
    const value = this.form?.get('invoice_format')?.value;
    return value && !invoiceFormatError(value) ? previewInvoiceId(value) : '';
  }

  get formatErrorMessage(): string | null {
    const control = this.form?.get('invoice_format');
    if (!control || (!control.dirty && !control.touched)) return null;
    if (control.hasError('required')) return 'Invoice format is required';
    if (control.hasError('maxlength')) return 'Invoice format must be at most 100 characters';
    return control.getError('invoiceFormat') ?? null;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const payload = this.form.getRawValue();
    const obs = this.editingUid
      ? this.httpService.updateBankAccount(this.editingUid, payload)
      : this.httpService.createBankAccount(payload);
    obs.subscribe({
      next: (resp) => {
        this.alert.success(resp?.message || 'Saved', 'toast-top-right');
        this.close();
        this.load();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to save bank account';
        this.alert.error(msg, 'toast-top-right');
      },
    });
  }

  remove(account: BankAccount): void {
    if (!confirm(`Delete bank account "${account.display_name}"?`)) return;
    this.httpService.deleteBankAccount(account.id).subscribe({
      next: (resp) => {
        this.alert.success(resp?.message || 'Deleted', 'toast-top-right');
        this.load();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to delete bank account';
        this.alert.error(msg, 'toast-top-right');
      },
    });
  }
}
